<!--
  SizesPanel — the Sizes host of the product editor's Sizes & Colors tab.

  Renamed + narrowed from SizesPricingPanel (2026-07-14 Sizes & Colors merge /
  Pricing split). The pricing machinery (margin card, upcharge table,
  suggested retail, on-mount margin fetch) moved to PricingPanel.vue on the
  new Pricing tab. This host keeps the SIZE + AVAILABILITY concerns:

    - Available-size chips driven by the product's ACTUAL size run (from the
      shared supplier meta), not a hardcoded XS-5XL grid (issue #2203), and
      their selection state (writes attributes.selected_sizes)
    - Selected sizes the supplier does not offer, flagged with a one-click
      remove (they have no chip to deselect)
    - One-size products (hats, bags, OSFA): a single "One size" readout,
      auto-selected, with no chip grid
    - Quantity-per-shopper override panel
      (writes attributes.quantity_per_shopper_override)
    - Visibility checkbox (writes attributes.visible)

  All writes go through store.updateField('<top-level-attr>', value).

  Size->pricing cleanup: deselecting a NON-standard size must prune its
  pricing data (upcharge + margin). That coupling lives in the store's
  deselectSize() action (authority-in-data) -- toggleSize() delegates to it on
  the deselect branch instead of reaching into pricing attributes here. So
  this host no longer writes any pricing-domain attribute, and the Pricing
  panel need not be mounted for the cleanup to run. (The store's deselectSize
  still classifies non-standard sizes via sizeConstants; this host no longer
  imports those constants because it no longer decides which chips render.)
-->
<template>
    <v-card data-testid="sizes-card">
        <v-card-title class="sizes-header">
            <v-icon icon="straighten" class="sizes-header-icon" />
            <div class="sizes-header-text">
                <div class="d-flex align-center">
                    <div class="sizes-title">Sizes</div>
                    <HelpIcon
                        audience="owner"
                        slug="set-up-a-product"
                        heading="step-2-choose-sizes-and-colors"
                        testid="help-icon-set-up-a-product-sizes"
                    />
                </div>
                <div class="sizes-subtitle">
                    Configure available sizes and shopper limits
                </div>
            </div>
        </v-card-title>

        <v-card-text>
            <!-- Sizes -->
            <div class="sizes-section">
                <label class="section-label">Available sizes</label>

                <!-- While supplier meta has not resolved we do NOT know the
                     product's real size run, so render a neutral placeholder
                     instead of synthesizing a wrong grid. -->
                <div
                    v-if="sizesLoading"
                    class="sizes-loading"
                    data-testid="sizes-loading"
                >
                    Loading sizes...
                </div>

                <!-- One-size products (hats, bags, OSFA): a single readout,
                     no per-size selection affordance. The lone size is
                     auto-selected (see the watch in script). -->
                <div
                    v-else-if="isOneSize"
                    class="sizes-one-size"
                    data-testid="sizes-one-size"
                >
                    One size
                </div>

                <!-- Multi-size products: one chip per actual available size,
                     in the supplier's order. -->
                <div v-else class="sizes-chips">
                    <button
                        v-for="size in availableSizes"
                        :key="size"
                        type="button"
                        :data-testid="`sizes-chip-${size}`"
                        :class="[
                            'sizes-chip',
                            { active: isSizeSelected(size) },
                        ]"
                        @click="toggleSize(size)"
                    >
                        {{ size }}
                    </button>
                </div>

                <!-- A selected size the supplier does not offer has no chip
                     (chips are the supplier's run), so it is listed here,
                     flagged, with a one-click remove (Jake, 2026-10-07). -->
                <ul
                    v-if="unavailableSizes.length"
                    class="sizes-unavailable"
                    data-testid="sizes-unavailable"
                >
                    <li
                        v-for="size in unavailableSizes"
                        :key="size"
                        class="sizes-unavailable__row"
                        :data-testid="`sizes-unavailable-${size}`"
                    >
                        <span class="sizes-unavailable__size">{{ size }}</span>
                        <span>Not available from {{ supplierName }}</span>
                        <button
                            type="button"
                            class="sizes-unavailable__remove"
                            :data-testid="`sizes-unavailable-remove-${size}`"
                            :aria-label="`Remove size ${size}`"
                            @click="store.deselectSize(size)"
                        >
                            Remove
                        </button>
                    </li>
                </ul>

                <!-- A Fixed-price product prices each size itself, so a size
                     added here has NO price until one is set on the Pricing
                     tab -- and shoppers cannot order it (#3884 gate P2-2). -->
                <p
                    v-if="unpricedSizes.length"
                    class="sizes-unpriced-warning"
                    data-testid="sizes-unpriced-warning"
                >
                    Fixed price: no price is set for
                    {{ unpricedSizes.join(', ') }}. Shoppers cannot order
                    {{ unpricedSizes.length === 1 ? 'this size' : 'these sizes' }}
                    until you set a price on the Pricing tab.
                </p>
            </div>

            <!-- Qty per shopper -->
            <div class="qty-section">
                <label class="section-label">Quantity per shopper</label>
                <div
                    v-if="!hasOverride"
                    class="qty-inherited"
                    data-testid="qty-inherited-readout"
                >
                    <v-icon icon="link" class="qty-icon" />
                    <template v-if="cpMinQty != null || cpMaxQty != null">
                        <strong>{{ cpMinQty ?? 1 }} – {{ cpMaxQty ?? '∞' }}</strong>
                        per shopper
                    </template>
                    <template v-else>
                        <strong>No limit</strong> · shoppers may buy any quantity
                    </template>
                </div>
                <label class="qty-override-toggle">
                    <input
                        type="checkbox"
                        data-testid="qty-override-checkbox"
                        :checked="hasOverride"
                        @change="onOverrideToggle($event)"
                    />
                    Override for this product
                </label>
                <div
                    v-if="hasOverride"
                    class="qty-override-panel"
                    data-testid="qty-override-panel"
                >
                    <div class="qty-override-fields">
                        <label class="qty-field">
                            <span class="qty-field-label">Min</span>
                            <input type="number" min="1" max="9999" data-testid="qty-min-input" :value="minDraft" class="qty-input" @input="onQtyMinChange($event)" />
                        </label>
                        <label class="qty-field">
                            <span class="qty-field-label">Max</span>
                            <input type="number" min="1" max="9999" data-testid="qty-max-input" :value="maxDraft" class="qty-input" @input="onQtyMaxChange($event)" />
                        </label>
                        <span class="qty-field-suffix">per shopper</span>
                    </div>
                    <p
                        v-if="qtyError"
                        class="qty-error"
                        data-testid="qty-error"
                    >
                        {{ qtyError }}
                    </p>
                </div>
            </div>

            <!-- Visibility -->
            <div class="visibility-section">
                <label class="vis-toggle">
                    <input
                        type="checkbox"
                        data-testid="visibility-checkbox"
                        :checked="isVisible"
                        @change="onVisibilityToggle($event)"
                    />
                    Visible on storefront
                </label>
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useProductEditorStore } from '@/stores/productEditor'
import { unpricedFixedSizes } from '@/pages/distributor/store-editor/StoreProducts/pricingMode'
import { supplierLabel } from '@/pages/distributor/store-editor/StoreProducts/supplierLabel'
import {
    findSameSize,
    sizesNotOffered,
    supplierSizeRun,
} from '@/pages/distributor/store-editor/StoreProducts/supplierSizes'
import HelpIcon from '@/components/Help/HelpIcon.vue'

const store = useProductEditorStore()

// Actual per-product available size run. Sourced from the SHARED supplier
// meta the store publishes (store.supplierMeta.sizes) -- the same source
// SupplierSkuPanel's "Available sizes" readout uses. We never synthesize a
// hardcoded XS-5XL grid: a hat, a bag, or a polo that comes S-XL must show
// exactly what the supplier offers (issue #2203).
//
// Fallback order for the chip set:
//   1. supplier meta sizes (authoritative)
//   2. the product's already-chosen selected_sizes (so a saved product with
//      no live meta still shows its real sizes, not a fabricated run)
//   3. empty -> the template renders the loading placeholder, never a grid.
const supplierSizes = computed(() => supplierSizeRun(store.supplierMeta))

const availableSizes = computed(() => {
    if (supplierSizes.value) return supplierSizes.value
    const saved = store.getEffectiveAttribute('selected_sizes')
    if (Array.isArray(saved) && saved.length > 0) return saved
    return []
})

// True while we do not yet have a usable size run: supplier meta is still
// loading (or unresolved) AND we have no saved fallback to show. Gate the
// chip render on this so we never flash a synthesized grid.
const sizesLoading = computed(
    () =>
        availableSizes.value.length === 0 &&
        (store.supplierMetaLoading || !store.supplierMetaReady),
)

// One-size products (hats, bags, OSFA-style single tokens): a single
// available size. These render a "One size" readout with no chip grid and
// no per-size affordance; the lone size is auto-selected below.
const isOneSize = computed(() => availableSizes.value.length === 1)

// Read EFFECTIVE attribute values (dirty if edited, else server) so chip
// selection reflects the user's in-flight edits immediately -- and so the
// Pricing tab, reading the SAME store getter, recomputes off the current
// size selection.
const selectedSizes = computed(
    () => store.getEffectiveAttribute('selected_sizes') ?? [],
)
// Selected sizes outside the supplier's run. Empty while the run is unknown
// (no meta, or a supplier with no catalog), where nothing can be judged.
const unavailableSizes = computed(() =>
    sizesNotOffered(selectedSizes.value, supplierSizes.value),
)
const supplierName = computed(
    () =>
        supplierLabel({ supplier_name: store.getEffectiveAttribute('supplier_name') })
        ?? 'the supplier',
)
const unpricedSizes = computed(() =>
    unpricedFixedSizes(
        store.getEffectiveAttribute('pricing_mode'),
        selectedSizes.value,
        store.getEffectiveAttribute('fixed_size_prices'),
    ),
)
const qtyOverride = computed(
    () => store.getEffectiveAttribute('quantity_per_shopper_override') ?? null,
)
const isVisible = computed(
    () => store.getEffectiveAttribute('visible') ?? true,
)

// One-size auto-select: when the product resolves to a single available
// size, ensure that size is the selected_sizes value without requiring a
// user click (there is no chip to click in the one-size state). Guarded so
// it only writes when the selection does not already match -- prevents a
// write loop / autosave thrash when meta re-resolves to the same size.
watch(
    [isOneSize, availableSizes],
    ([oneSize, sizes]) => {
        if (!oneSize) return
        const only = sizes[0]
        const current = selectedSizes.value
        if (current.length === 1 && current[0] === only) return
        store.updateField('selected_sizes', [only])
    },
    { immediate: true },
)

const hasOverride = computed(() => qtyOverride.value != null)
const overrideMin = computed(() => qtyOverride.value?.min ?? 1)
const overrideMax = computed(() => qtyOverride.value?.max ?? 5)

// CP-level default per-shopper qty limits. When no override is set, the
// editor surfaces these as the effective limit.
const cpMinQty = computed(() => store.getEffectiveAttribute('min_quantity'))
const cpMaxQty = computed(() => store.getEffectiveAttribute('max_quantity'))

const qtyError = ref(null)

// Local draft refs for qty min/max inputs so a rejected user value persists
// in the input rather than snapping back to the store value.
const minDraft = ref(overrideMin.value)
const maxDraft = ref(overrideMax.value)
watch(overrideMin, (v) => {
    minDraft.value = v
})
watch(overrideMax, (v) => {
    maxDraft.value = v
})

// Alias-aware: a saved 2XL selects the supplier's XXL chip (and the reverse),
// so the chip never reads unselected for a size that is in fact selected.
function selectedLabelFor(size) {
    return findSameSize(selectedSizes.value, size)
}

function isSizeSelected(size) {
    return selectedLabelFor(size) != null
}

function toggleSize(size) {
    const selectedLabel = selectedLabelFor(size)
    if (selectedLabel != null) {
        // Delegate the deselect (and, for non-standard sizes, the pricing
        // cleanup) to the store so the size->pricing coupling is
        // single-sourced (authority-in-data). This host no longer writes
        // pricing-domain attributes directly. The SAVED label is removed,
        // which is what keeps an XXL click from adding a second 2XL.
        store.deselectSize(selectedLabel)
        return
    }
    // Select branch is a plain selected_sizes write -- there is no pricing
    // state to seed on selection.
    const current = new Set(selectedSizes.value)
    current.add(size)
    store.updateField('selected_sizes', Array.from(current))
}

function onOverrideToggle(e) {
    if (e.target.checked) {
        store.updateField('quantity_per_shopper_override', {
            min: 1,
            max: 5,
        })
    } else {
        store.updateField('quantity_per_shopper_override', null)
    }
    qtyError.value = null
}

function onQtyMinChange(e) {
    minDraft.value = e.target.value
    const v = parseInt(e.target.value, 10)
    if (!Number.isFinite(v) || v < 1 || v > 9999) {
        qtyError.value = 'Min must be a positive integer'
        return
    }
    if (v > overrideMax.value) {
        qtyError.value = 'Min cannot exceed max'
        return
    }
    qtyError.value = null
    store.updateField('quantity_per_shopper_override', {
        min: v,
        max: overrideMax.value,
    })
}

function onQtyMaxChange(e) {
    maxDraft.value = e.target.value
    const v = parseInt(e.target.value, 10)
    if (!Number.isFinite(v) || v < 1 || v > 9999) {
        qtyError.value = 'Max must be a positive integer'
        return
    }
    if (v < overrideMin.value) {
        qtyError.value = 'Max cannot be less than min'
        return
    }
    qtyError.value = null
    store.updateField('quantity_per_shopper_override', {
        min: overrideMin.value,
        max: v,
    })
}

function onVisibilityToggle(e) {
    store.updateField('visible', e.target.checked)
}
</script>

<style scoped>
.sizes-header {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
}

.sizes-header-icon {
    flex: 0 0 auto;
    font-size: 28px;
}

.sizes-header-text {
    flex: 1 1 auto;
    min-width: 0;
}

.sizes-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 1.2;
}

.sizes-subtitle {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 2px;
}

.section-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 6px;
}

.sizes-section,
.qty-section,
.visibility-section {
    margin-bottom: 20px;
}

.sizes-unpriced-warning {
    font-size: 13px;
    color: #8a4b00;
    background: #fff4e0;
    border: 1px solid #f0c27a;
    border-radius: 6px;
    padding: 8px 12px;
    margin: 12px 0 0;
}

.sizes-unavailable {
    list-style: none;
    padding: 0;
    margin: 12px 0 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sizes-unavailable__row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #8a4b00;
    background: #fff4e0;
    border: 1px solid #f0c27a;
    border-radius: 6px;
    padding: 6px 12px;
}

.sizes-unavailable__size {
    font-weight: 600;
}

.sizes-unavailable__remove {
    margin-left: auto;
    background: white;
    border: 1px solid #f0c27a;
    border-radius: 4px;
    padding: 2px 10px;
    cursor: pointer;
    font: inherit;
}

.sizes-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.sizes-loading {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    font-style: italic;
}

.sizes-one-size {
    display: inline-flex;
    align-items: center;
    background: var(--tf-primary-soft);
    border-radius: 16px;
    padding: 6px 14px;
    font-size: 14px;
    font-weight: 600;
}

.sizes-chip {
    background: white;
    border: 1px solid var(--tf-slate-border);
    border-radius: 16px;
    padding: 6px 14px;
    cursor: pointer;
    font: inherit;
    line-height: 1.2;
}

.sizes-chip.active {
    background: var(--tf-slate-ink);
    color: white;
    border-color: var(--tf-slate-ink);
}

.qty-inherited {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    background: var(--tf-primary-soft);
    border-radius: 6px;
    padding: 10px 12px;
    margin: 6px 0;
    font-size: 14px;
}

.qty-icon {
    margin-right: 4px;
}

.qty-override-toggle,
.vis-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    margin-top: 4px;
    font-size: 14px;
}

/* Neutral setting styling (2026-07-21, Jake): the override min/max is a
   normal setting, not a warning -- the old --tf-warning-soft amber tint read
   as an error state. Render as a plain inline field with explicit Min/Max
   labels so it reads as "set a range", not "something is wrong". */
.qty-override-panel {
    margin: 6px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.qty-override-fields {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}

.qty-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.qty-field-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
}

.qty-field-suffix {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    padding-bottom: 6px;
}

.qty-input {
    width: 80px;
    padding: 4px 8px;
    border: 1px solid var(--tf-slate-border);
    border-radius: 4px;
    font: inherit;
}

.qty-error {
    color: var(--tf-error-dark);
    font-size: 12px;
    margin: 4px 0 0;
    width: 100%;
}
</style>
